import { DateTime } from 'luxon';
import React, { useMemo } from 'react';

import { InfoCard, LinkButton } from '@backstage/core-components';

import { Box, Typography, CircularProgress } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';

import { useScorecardData } from '../../context/ScorecardDataContext';
import { CategoryData, CategoryScore } from '../../types';
import {
  buildScorecardHierarchy,
  GroupedCategoryScore,
} from '../../utils/scorecardHierarchyUtils';
import { SCORECARD_HIERARCHY } from '../ScorecardCategories/scorecardHierarchy';

const useStyles = makeStyles(theme => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    '& > div:last-child': {
      padding: theme.spacing(3),
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      '& > div': {
        padding: '0 !important',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      },
    },
  },
  contentWrapper: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  gridLayout: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(4),
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    height: '100%',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: theme.spacing(2),
    },
  },
  chartWrapper: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    minWidth: 0,
    gap: theme.spacing(1),
  },
  chartContainer: {
    position: 'relative',
    width: '100%',
    height: 'auto',
    maxWidth: '280px',
    aspectRatio: '1 / 1',
  },
  chartSvg: {
    width: '100%',
    height: '100%',
    transform: 'rotate(-90deg)',
  },
  chartCenter: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  averageScore: {
    fontSize: 'clamp(24px, 4vw, 48px)',
    fontWeight: 700,
    lineHeight: 1,
  },
  monthLabel: {
    fontSize: '0.78rem',
    fontWeight: 500,
    color: theme.palette.text.secondary,
    textAlign: 'center',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  statsContainer: {
    flex: 1.2,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'center',
    gap: theme.spacing(1),
  },
  statRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  groupTitle: {
    fontWeight: 600,
    fontSize: '1rem',
    lineHeight: 1.2,
  },
  categoriesCount: {
    fontSize: '0.8rem',
    lineHeight: 1.4,
  },
  groupScore: {
    fontWeight: 700,
    fontSize: '1.1rem',
  },
  high: { color: theme.palette.success.main },
  medium: { color: theme.palette.warning.main },
  low: { color: theme.palette.error.main },
  na: { color: theme.palette.text.disabled },
}));

const toCategoryScores = (categoriesData: CategoryData[]): CategoryScore[] => {
  return categoriesData
    .map(categoryData => {
      const period = categoryData.data?.periods?.[0];
      if (!period) return null;

      const pct = period.value?.aggregated_category_score_percentage;
      if (pct === undefined || pct === -1) return null;

      const score = Number((pct * 100).toFixed(2));
      const tier = period.value?.aggregated_category_badge ?? 'N/A';

      return {
        category: categoryData.category,
        score,
        tier,
        description: '',
        checks: [],
      } as CategoryScore;
    })
    .filter((s): s is CategoryScore => s !== null);
};

const countLeafCategories = (node: GroupedCategoryScore): number => {
  if (node.type === 'category') {
    return node.score !== 'N/A' ? 1 : 0;
  }
  return (node.children ?? []).reduce(
    (sum, child) => sum + countLeafCategories(child),
    0,
  );
};

export const ScorecardOverview: React.FC = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { categoriesData, loading } = useScorecardData();

  const currentMonthLabel = DateTime.now().toFormat('MMMM yyyy');
  const hierarchicalScores = useMemo(() => {
    if (!categoriesData) return [];
    const categoryScores = toCategoryScores(categoriesData);
    return buildScorecardHierarchy(categoryScores);
  }, [categoriesData]);

  const groupStats = useMemo(() => {
    return SCORECARD_HIERARCHY.map(hierarchyNode => {
      const built = hierarchicalScores.find(h => h.name === hierarchyNode.name);
      return {
        name: hierarchyNode.displayName,
        score: built?.score ?? 'N/A',
        count: built ? countLeafCategories(built) : 0,
      };
    });
  }, [hierarchicalScores]);

  const totalAverage = useMemo(() => {
    const validScores = groupStats
      .filter(g => g.score !== 'N/A')
      .map(g => g.score as number);
    return validScores.length
      ? Math.round(validScores.reduce((a, b) => a + b, 0) / validScores.length)
      : 0;
  }, [groupStats]);

  if (loading)
    return (
      <InfoCard title="Scorecards Summary">
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress size={24} />
        </Box>
      </InfoCard>
    );

  const getScoreColorClass = (score: number | string) => {
    if (score === 'N/A') return classes.na;
    if (typeof score === 'number' && score >= 80) return classes.high;
    if (typeof score === 'number' && score >= 50) return classes.medium;
    return classes.low;
  };

  return (
    <InfoCard
      title="Scorecards Summary"
      variant="gridItem"
      className={classes.root}
      headerProps={{ titleTypographyProps: { variant: 'h6' } }}
      action={
        <Box display="flex">
          <LinkButton variant="outlined" color="primary" to="../scorecards">
            View Scorecards
          </LinkButton>
        </Box>
      }
    >
      <Box className={classes.contentWrapper}>
        <Box className={classes.gridLayout}>
          <Box className={classes.chartWrapper}>
            <Box className={classes.chartContainer}>
              <svg viewBox="0 0 100 100" className={classes.chartSvg}>
                <circle
                  cx="50"
                  cy="50"
                  r="42"
                  fill="none"
                  stroke={theme.palette.divider}
                  strokeWidth="10"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="42"
                  fill="none"
                  stroke={
                    totalAverage >= 80
                      ? theme.palette.success.main
                      : theme.palette.warning.main
                  }
                  strokeWidth="10"
                  strokeDasharray={`${(totalAverage / 100) * 263.8} 263.8`}
                  strokeLinecap="round"
                />
              </svg>
              <Box className={classes.chartCenter}>
                <Typography className={classes.averageScore}>
                  {totalAverage}%
                </Typography>
              </Box>
            </Box>

            <Typography className={classes.monthLabel}>
              {currentMonthLabel}
            </Typography>
          </Box>

          <Box className={classes.statsContainer}>
            {groupStats.map(group => (
              <Box key={group.name} className={classes.statRow}>
                <Box>
                  <Typography className={classes.groupTitle}>
                    {group.name}
                  </Typography>
                  <Typography
                    color="textSecondary"
                    className={classes.categoriesCount}
                  >
                    {group.count} underlying categories
                  </Typography>
                </Box>
                <Typography
                  className={`${classes.groupScore} ${getScoreColorClass(
                    group.score,
                  )}`}
                >
                  {group.score !== 'N/A' ? `${group.score}%` : 'N/A'}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </InfoCard>
  );
};
